import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { ProgramService } from "../../services/ProgramService";
import { SubjectService } from "../../services/SubjectService";
import { UserDetailContext } from "../../utils/Context";
import { MULTI, Selector, SINGLE } from "./Selector";

export const ProgramSelect = ({
  selectHandler,
  size,
  clear,
  type,
  width = null,
  selectedHierarchies = null,
  selectedPrograms = null,
  forProgramAssessment = true,
  isRequired = true,
  label = null,
  placeholder = null,
  selectedSubjectId = null,
}) => {
  const userDetails = useContext(UserDetailContext);
  const [programs, setPrograms] = useState([]);
  const [cachedPrograms, setCachedPrograms] = useState({});
  const enriched = true;

  const appendProgramTypeAbbr = (programs) => {
    if (!enriched) {
      return programs;
    }
    let prog_with_type = programs.map((prog) => {
      prog.label = prog.abbreviation
        ? prog.degreeName + " (" + prog.abbreviation + ")"
        : prog.degreeName;
      return prog;
    });
    return prog_with_type;
  };

  useEffect(() => {
    if (selectedSubjectId?.length > 0) {
      if (selectedSubjectId in cachedPrograms) {
        setPrograms(cachedPrograms[selectedSubjectId]);
      } else {
        ProgramService.getProgramsByAcSub(selectedSubjectId).then(
          (programs) => {
            let fullProgramNames = appendProgramTypeAbbr(programs);
            setPrograms(fullProgramNames);
            setCachedPrograms((prevFetched) => ({
              ...prevFetched,
              [selectedSubjectId]: fullProgramNames,
            }));
          }
        );
      }
    } else if (userDetails.isAC()) {
      if (selectedHierarchies?.length > 0) {
        handleProgramsByHierarchy();
      } else {
        ProgramService.getPrograms().then((programs) => {
          let fullProgramNames = appendProgramTypeAbbr(programs);
          setPrograms(fullProgramNames);
        });
      }
    } else if (userDetails.isPC() || userDetails.isFaculty()) {
      if (selectedHierarchies?.length > 0) {
        handleProgramsByHierarchy();
      } else {
        const programCalls = [];
        SubjectService.getSubjects().then((subjects) => {
          subjects.forEach((s) => {
            if (userDetails.ac_subjects.includes(s.title)) {
              programCalls.push(ProgramService.getProgramsByAcSub(s.id));
            }
          });
          Promise.all(programCalls).then((programs) => {
            programs = programs.filter((p) => p.length).flat();
            let fullProgramNames = appendProgramTypeAbbr(programs);
            setPrograms(fullProgramNames);
          });
        });
      }
    }
  }, [userDetails, selectedHierarchies, selectedSubjectId]);

  const handleProgramsByHierarchy = () => {
    const programCalls = [];
    const hierarchySubjects = [];
    selectedHierarchies.forEach((hierarchy) => {
      hierarchySubjects.push(...hierarchy.subjects);
    });
    const uniqueSubjects = hierarchySubjects.filter(
      (elem, index) =>
        hierarchySubjects.findIndex((obj) => obj.id === elem.id) === index
    );
    uniqueSubjects.forEach((s) => {
      if (userDetails.isAC()) {
        programCalls.push(ProgramService.getProgramsByAcSub(s.id));
      } else {
        if (userDetails.ac_subjects.includes(s.title)) {
          programCalls.push(ProgramService.getProgramsByAcSub(s.id));
        }
      }
    });
    Promise.all(programCalls).then((programs) => {
      programs = programs.filter((p) => p.length).flat();
      let fullProgramNames = appendProgramTypeAbbr(programs);
      setPrograms(fullProgramNames);
    });
  };

  return type === SINGLE ? (
    <Selector
      type={SINGLE}
      id="program-select"
      label={label ?? t("programs.program") + "*"}
      options={programs}
      selectHandler={selectHandler}
      assistiveText={t("programs.selectProgramAssistive")}
      size={size}
      clear={clear}
      width={width}
    />
  ) : (
    <Selector
      type={MULTI}
      id={"program-select"}
      label={label ?? t("programs.programs") + (isRequired ? "*" : "")}
      options={programs}
      selectHandler={selectHandler}
      selectedOptions={selectedPrograms}
      clear={clear}
      size={size}
      interaction={!!forProgramAssessment && !!programs.length}
      placeholder={placeholder}
      width={width}
    />
  );
};
